import React, { useCallback, useEffect } from "react";
import Layout from "../../../Components/Layout";
import ProGrid from "../../../Components/ProGrid/ProGrid";
import useColumn from "../../../../utils/customHooks/useColumns";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import { successToast, errorToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import useDebouncedFilter from "../../../../utils/customHooks/useDebouncedFilter";
import { STAUTES } from "../../../../utils/constant";
import { ADMIN_RAP_INFO } from "../../../../utils/services/apiPath";
import { deleteApi, getApi } from "../../../../utils/services";
import { createODataQuery, convertMuiFilterToOData } from "../../../../utils/services/oData";
import StandardToolbar from "../../../Components/Toolbars/StandardToolbar";
import { apprenticeRapPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";

const getRedirectUrl = params => {
  return `/apprentice-180/rap/info/${params.id}`;
};

export default function RapList() {
  const {
    currentPage,
    pageSize,
    data: rapData,
    rowCount,
    status,
    sortModel,
    searchString,
    filterModel,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString,
    setFilterModel
  } = usePageState();
  const onFilterChange = useDebouncedFilter(setFilterModel);
  const { "Rap Info List": rapInfoListPermission } = usePermission(apprenticeRapPermissionSelector, "Rap Info List");
  const { canView, canAdd, canExport, canDelete } = rapInfoListPermission || {};

  const columns = useColumn(
    [
      {
        field: "approvedJobTitle",
        headerName: "RAP Title",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
      {
        field: "industryType",
        headerName: "Industry"
      },
      {
        field: "category",
        headerName: "Category"
      },
      {
        field: "rapidCode",
        headerName: "RAPIDS Code"
      },
      {
        field: "programLengthYears",
        headerName: "Program Length"
      },
      {
        field: "rapInfoId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell title={"Delete Rap Info"} params={params} onDelete={onDelete} disabled={params?.row?.readOnly} />
      }
    ],
    canDelete
  );

  const onDelete = async id => {
    const { error } = await deleteApi(`${ADMIN_RAP_INFO}/${id}`);
    if (error) return errorToast(error);
    successToast("Rap Info deleted successfully.");
    getRapInfoList();
  };

  const getRapInfoList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["approvedJobTitle", "industryType", "rapidCode", "category"];
    
    // Convert MUI filter model to OData filter
    const gridFilterQuery = convertMuiFilterToOData(filterModel);
    
    // Combine queries including grid filter
    const allQueries = [gridFilterQuery].filter(q => q && q.length > 0);
    
    const { data, totalRecords, error } = await getApi(
      ADMIN_RAP_INFO + createODataQuery(searchableFields, [], allQueries, searchString, sortModel, pageSize, currentPage)
    );
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, filterModel, setPageState, setStatus]);

  useEffect(() => {
    getRapInfoList();
  }, [getRapInfoList]);

  return (
    <Layout canView={canView}>
      <ProGrid
        loading={status === STAUTES.LOADING}
        options={{
          getRowId: row => row.rapInfoId,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          filteringMode: "server",
          onSortModelChange: setSortModel,
          onFilterModelChange: onFilterChange
        }}
        columns={columns}
        rows={rapData}
        searchMode={!!searchString}
        toolbar={StandardToolbar({
          showAddButton: canAdd,
          showExportButton: canExport,
          toolbarTitle: "Rap Info",
          addButtonUrl: "/apprentice-180/rap/info/new",
          addButtonTitle: "+ Add Rap Info",
          searchModel: { defaultValue: searchString, handleDebounce: setSearchString }
        })}
      />
    </Layout>
  );
}
